import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  InputGroup,
} from "react-bootstrap";
import { FaSave, FaSyncAlt, FaArrowLeft, FaFileAlt, FaCog, FaCheck, FaInfoCircle, FaPlus, FaTrash } from "react-icons/fa";
import { BsGear } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, getFirestore, serverTimestamp, setDoc } from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);

const DEFAULT_CODES = {
  rem: {
    horas_extras_25: "0105",
    horas_extras_35: "0106",
    horas_extras_100: "0107",
    remuneracion_vacacional: "0118",
    remuneracion_basica: "0121",
    remuneracion_permanente: "0122",
    asignacion_familiar: "0201",
    bonificaciones_regulares: "0306",
    devolucion_renta_5ta: "0928",
    adelanto: "0701",
    cuota_sindical: "0702",
    otros_descuentos: "0706",
    spp_comision: "0601",
    conafovicer: "0602",
    renta_5ta: "0605",
    prima_seguro_afp: "0606",
    spp_aporte_obligatorio: "0608",
    spp_aporte_voluntario: "0609",
    otros_aportes_trabajador: "0611",
    spp_aporte_voluntario_empleador: "0801",
    vida_ley: "0803",
    otras_aportaciones_empleador: "0809",
    sis_empleador: "0811",
  },
};

const FIELDS = [
  {
    id: "ingresos_rem",
    title: "Ingresos (.rem)",
    key: "rem",
    items: [
      ["horas_extras_25", "Trabajo en sobretiempo 25%"],
      ["horas_extras_35", "Trabajo en sobretiempo 35%"],
      ["horas_extras_100", "Trabajo día feriado / descanso"],
      ["remuneracion_vacacional", "Remuneración vacacional"],
      ["remuneracion_basica", "Remuneración o jornal básico"],
      ["remuneracion_permanente", "Remuneración permanente"],
      ["asignacion_familiar", "Asignación familiar"],
      ["bonificaciones_regulares", "Bonificaciones regulares"],
      ["devolucion_renta_5ta", "Devolución retención exceso 5ta"],
    ],
  },
  {
    id: "descuentos_rem",
    title: "Descuentos (.rem)",
    key: "rem",
    items: [
      ["adelanto", "Adelanto"],
      ["cuota_sindical", "Cuota sindical"],
      ["otros_descuentos", "Otros descuentos no deducibles"],
    ],
  },
  {
    id: "aportes_trabajador_rem",
    title: "Aportes Trabajador (.rem)",
    key: "rem",
    items: [
      ["spp_comision", "SPP Comisión AFP"],
      ["conafovicer", "CONAFOVICER"],
      ["renta_5ta", "Renta 5ta categoría"],
      ["prima_seguro_afp", "Prima seguro AFP"],
      ["spp_aporte_obligatorio", "SPP aporte obligatorio"],
      ["spp_aporte_voluntario", "SPP aporte voluntario"],
      ["otros_aportes_trabajador", "Otros aportes trabajador/pensionista"],
    ],
  },
  {
    id: "aportes_empleador_rem",
    title: "Aportes Empleador (.rem)",
    key: "rem",
    items: [
      ["spp_aporte_voluntario_empleador", "SPP aporte voluntario empleador"],
      ["vida_ley", "Póliza seguro vida ley"],
      ["otras_aportaciones_empleador", "Otras aportaciones empleador"],
      ["sis_empleador", "Seguro Integral de Salud (SIS)"],
    ],
  },
];

const SECTION_GRADIENTS = [
  "linear-gradient(135deg, #D2691E 0%, #CD853F 100%)",
  "linear-gradient(135deg, #20c997 0%, #17a2b8 100%)",
  "linear-gradient(135deg, #fd7e14 0%, #e83e8c 100%)",
  "linear-gradient(135deg, #6f42c1 0%, #6610f2 100%)",
  "linear-gradient(135deg, #495057 0%, #343a40 100%)",
];

const PLANILLA_SOURCE_OPTIONS = [
  { value: "computed.remuneracion_basica", label: "Remuneración básica (ajustada por subsidio)" },
  { value: "computed.subsidio_incapacidad", label: "Subsidio por incapacidad (exceso 20 días)" },
  { value: "computed.remuneracion_vacacional", label: "Remuneración vacacional (vacaciones/grati trunca)" },
  { value: "sueldo_base", label: "Sueldo base" },
  { value: "sueldo_permanente", label: "Sueldo permanente" },
  { value: "asignacion_familiar", label: "Asignación familiar" },
  { value: "ingresos.horas_extras_25", label: "Ingresos: Horas extras 25%" },
  { value: "ingresos.horas_extras_35", label: "Ingresos: Horas extras 35%" },
  { value: "ingresos.horas_extras_100", label: "Ingresos: Horas extras 100%" },
  { value: "he_25_monto", label: "Horas extras 25% (campo legado)" },
  { value: "he_35_monto", label: "Horas extras 35% (campo legado)" },
  { value: "he_100_monto", label: "Horas extras 100% (campo legado)" },
  { value: "ingresos.bonificacion_regular", label: "Ingresos: Bonificación regular" },
  { value: "devolucion_renta_5ta", label: "Devolución retención renta 5ta" },
  { value: "descuentos.adelantos", label: "Descuentos: Adelantos" },
  { value: "descuentos.cuota_sindical", label: "Descuentos: Cuota sindical" },
  { value: "descuentos.otros_descuentos", label: "Descuentos: Otros descuentos" },
  { value: "descuentos.renta_5ta", label: "Descuentos: Renta 5ta" },
  { value: "descuentos.conafovicer", label: "Descuentos: CONAFOVICER" },
  { value: "descuentos.sistema_pension.comision", label: "Sistema pensión: Comisión AFP" },
  { value: "descuentos.sistema_pension.seguro", label: "Sistema pensión: Seguro AFP" },
  { value: "descuentos.sistema_pension.fondo", label: "Sistema pensión: Fondo AFP" },
  { value: "descuentos.sistema_pension.voluntario", label: "Sistema pensión: Aporte voluntario" },
  { value: "descuentos.otros_aportes_trabajador", label: "Descuentos: Otros aportes trabajador" },
  { value: "aportaciones.voluntario_empleador", label: "Aportaciones: SPP voluntario empleador" },
  { value: "aportaciones.vida_ley", label: "Aportaciones: Vida ley" },
  { value: "aportaciones.sctr", label: "Aportaciones: SCTR" },
  { value: "aportaciones.otras", label: "Aportaciones: Otras aportaciones" },
  { value: "aportaciones.essalud", label: "Aportaciones: EsSalud" },
  { value: "aportaciones.sis", label: "Aportaciones: SIS" },
];

const SECTION_SOURCE_GROUP = {
  ingresos_rem: "ingresos",
  descuentos_rem: "descuentos",
  aportes_trabajador_rem: "descuentos",
  aportes_empleador_rem: "aportaciones",
};

const SOURCE_OPTIONS_BY_GROUP = {
  ingresos: PLANILLA_SOURCE_OPTIONS.filter((o) =>
    o.value.startsWith("ingresos.") ||
    o.value.startsWith("computed.") ||
    o.value === "sueldo_base" ||
    o.value === "sueldo_permanente" ||
    o.value === "asignacion_familiar" ||
    o.value === "devolucion_renta_5ta" ||
    o.value === "he_25_monto" ||
    o.value === "he_35_monto" ||
    o.value === "he_100_monto"
  ),
  descuentos: PLANILLA_SOURCE_OPTIONS.filter((o) =>
    o.value.startsWith("descuentos.")
  ),
  aportaciones: PLANILLA_SOURCE_OPTIONS.filter((o) =>
    o.value.startsWith("aportaciones.")
  ),
};

const DEFAULT_SOURCE_PATH_BY_KEY = {
  remuneracion_basica: "computed.remuneracion_basica",
  remuneracion_permanente: "sueldo_permanente",
  horas_extras_25: "ingresos.horas_extras_25",
  horas_extras_35: "ingresos.horas_extras_35",
  horas_extras_100: "ingresos.horas_extras_100",
  remuneracion_vacacional: "computed.remuneracion_vacacional",
  asignacion_familiar: "asignacion_familiar",
  bonificaciones_regulares: "ingresos.bonificacion_regular",
  subsidio_incapacidad: "computed.subsidio_incapacidad",
  devolucion_renta_5ta: "devolucion_renta_5ta",
  adelanto: "descuentos.adelantos",
  cuota_sindical: "descuentos.cuota_sindical",
  otros_descuentos: "descuentos.otros_descuentos",
  spp_comision: "descuentos.sistema_pension.comision",
  conafovicer: "descuentos.conafovicer",
  prima_seguro_afp: "descuentos.sistema_pension.seguro",
  spp_aporte_obligatorio: "descuentos.sistema_pension.fondo",
  spp_aporte_voluntario: "descuentos.sistema_pension.voluntario",
  otros_aportes_trabajador: "descuentos.otros_aportes_trabajador",
  renta_5ta: "descuentos.renta_5ta",
  spp_aporte_voluntario_empleador: "aportaciones.voluntario_empleador",
  vida_ley: "aportaciones.vida_ley",
  otras_aportaciones_empleador: "aportaciones.sctr",
  essalud_empleador: "aportaciones.essalud",
  sis_empleador: "aportaciones.sis",
};

const getSourceOptionsForSection = (sectionId) => {
  const group = SECTION_SOURCE_GROUP[sectionId] || "ingresos";
  return SOURCE_OPTIONS_BY_GROUP[group] || [];
};

const getDefaultSourcePathForSection = (sectionId, key = "") => {
  const preferred = DEFAULT_SOURCE_PATH_BY_KEY[key];
  const options = getSourceOptionsForSection(sectionId);
  const values = new Set(options.map((opt) => opt.value));
  if (preferred && values.has(preferred)) return preferred;
  return options[0]?.value || "sueldo_base";
};

const isSourcePathValidForSection = (sectionId, sourcePath) => {
  const options = getSourceOptionsForSection(sectionId);
  const values = new Set(options.map((opt) => opt.value));
  return values.has(sourcePath);
};

const DEFAULT_CATALOG = FIELDS.reduce((acc, section) => {
  acc[section.id] = section.items.map(([key, label]) => ({
    key,
    label,
    source_path: getDefaultSourcePathForSection(section.id, key),
  }));
  return acc;
}, {});

const normalizeCodes = (source = null) => {
  if (!source || typeof source !== "object") return { ...DEFAULT_CODES };
  const rem = source.rem && typeof source.rem === "object" ? source.rem : DEFAULT_CODES.rem;
  return {
    rem: { ...rem },
  };
};

const normalizeCatalog = (source = null) => {
  const base = JSON.parse(JSON.stringify(DEFAULT_CATALOG));
  if (!source || typeof source !== "object") return base;
  const normalized = { ...base };

  FIELDS.forEach((section) => {
    const items = source[section.id];
    const sectionOptions = getSourceOptionsForSection(section.id);
    const sectionValues = new Set(sectionOptions.map((opt) => opt.value));
    if (Array.isArray(items)) {
      normalized[section.id] = items
        .filter((item) => item && typeof item === "object")
        .map((item) => ({
          key: String(item.key || "").trim(),
          label: String(item.label || "").trim(),
          source_path: sectionValues.has(item.source_path)
            ? item.source_path
            : getDefaultSourcePathForSection(section.id, String(item.key || "").trim()),
        }))
        .filter((item) => item.key);
    }
  });

  return normalized;
};

const createCustomKey = () => `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const ConfiguracionPdtPlame = ({ usuario }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [codes, setCodes] = useState(DEFAULT_CODES);
  const [catalog, setCatalog] = useState(DEFAULT_CATALOG);
  const [lastSaved, setLastSaved] = useState(null);
  const invalidBindingsCount = useMemo(
    () =>
      FIELDS.reduce((count, section) => {
        const invalidInSection = (catalog[section.id] || []).filter(
          (item) => !isSourcePathValidForSection(section.id, item.source_path)
        ).length;
        return count + invalidInSection;
      }, 0),
    [catalog]
  );

  const ruc = useMemo(() => String(usuario?.ruc || ""), [usuario?.ruc]);

  const cargar = useCallback(async () => {
    if (!ruc) return;
    setLoading(true);
    setError("");
    setOkMsg("");
    try {
      const refDoc = doc(db, "pl_pdt_plame", ruc);
      const snap = await getDoc(refDoc);
      if (!snap.exists()) {
        setCodes(DEFAULT_CODES);
        setCatalog(DEFAULT_CATALOG);
        setLastSaved(null);
      } else {
        const data = snap.data() || {};
        const cfg = data.codigos || data.codes || data;
        setCodes(normalizeCodes(cfg));
        setCatalog(normalizeCatalog(data.catalogo));

        if (data.updated_at?.toDate) {
          setLastSaved(data.updated_at.toDate());
        } else {
          setLastSaved(null);
        }
      }
    } catch (e) {
      console.error("Error cargando pl_pdt_plame:", e);
      setError("No se pudo cargar la configuración PDT PLAME.");
    } finally {
      setLoading(false);
    }
  }, [ruc]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const setCode = (scope, key, value) => {
    const clean = String(value || "").replace(/[^\d]/g, "").slice(0, 4);
    setCodes((prev) => ({
      ...prev,
      [scope]: {
        ...prev[scope],
        [key]: clean,
      },
    }));
  };

  const setLabel = (sectionId, key, value) => {
    setCatalog((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map((item) =>
        item.key === key ? { ...item, label: value } : item
      ),
    }));
  };

  const setSourcePath = (sectionId, key, value) => {
    setCatalog((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).map((item) =>
        item.key === key ? { ...item, source_path: value } : item
      ),
    }));
  };

  const addField = (sectionId, scope) => {
    const key = createCustomKey();
    setCatalog((prev) => ({
      ...prev,
      [sectionId]: [
        ...(prev[sectionId] || []),
        {
          key,
          label: "Nuevo código",
          source_path: getDefaultSourcePathForSection(sectionId, key),
        },
      ],
    }));
    setCodes((prev) => ({
      ...prev,
      [scope]: {
        ...(prev[scope] || {}),
        [key]: "",
      },
    }));
  };

  const removeField = (sectionId, scope, key) => {
    setCatalog((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || []).filter((item) => item.key !== key),
    }));
    setCodes((prev) => {
      const scopeCodes = { ...(prev[scope] || {}) };
      delete scopeCodes[key];
      return {
        ...prev,
        [scope]: scopeCodes,
      };
    });
  };

  const guardar = async () => {
    if (!ruc) return;
    if (invalidBindingsCount > 0) {
      setError("Hay códigos con variable de planilla no válida para su sección. Corrija los campos en rojo.");
      return;
    }
    setSaving(true);
    setError("");
    setOkMsg("");
    try {
      const refDoc = doc(db, "pl_pdt_plame", ruc);
      await setDoc(
        refDoc,
        {
          ruc_empresa: ruc,
          codigos: codes,
          catalogo: catalog,
          updated_at: serverTimestamp(),
          updated_by: usuario?.dni || usuario?.email || "sistema",
        },
        { merge: true }
      );
      setLastSaved(new Date());
      setOkMsg("Configuración PDT PLAME guardada correctamente.");
    } catch (e) {
      console.error("Error guardando pl_pdt_plame:", e);
      setError("No se pudo guardar la configuración. Verifique permisos de Firestore.");
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setCodes(DEFAULT_CODES);
    setCatalog(DEFAULT_CATALOG);
    setOkMsg("");
    setError("");
  };

  if (loading) {
    return (
      <div
        className="min-vh-100 d-flex justify-content-center align-items-center"
        style={{
          background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        }}
      >
        <div className="text-center">
          <Spinner animation="border" style={{ color: "#8B4513", width: "3rem", height: "3rem" }} />
          <p className="mt-3 mb-0" style={{ color: "#8B4513" }}>
            Cargando configuración...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <BsGear className="me-3" />
                Configuración PDT PLAME
              </h2>
              <p className="mb-0 opacity-75">Administre los códigos oficiales por empresa para exportación .REM</p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center g-3">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                        color: "white",
                      }}
                    >
                      <FaCog size={24} />
                    </div>
                  </Col>
                  <Col md={6}>
                    <h5 className="mb-1 fw-semibold">Parámetros de Configuración</h5>
                    <p className="text-muted mb-0 small">Personalice los códigos PDT PLAME de esta empresa (RUC: {ruc || "Sin RUC"})</p>
                  </Col>
                  <Col md={5} className="text-md-end">
                    <div className="d-flex gap-2 justify-content-md-end flex-wrap">
                      {lastSaved && (
                        <Badge
                          style={{
                            background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                            fontSize: "0.8rem",
                            padding: "6px 12px",
                          }}
                        >
                          <FaCheck className="me-1" />
                          Última actualización: {lastSaved.toLocaleString()}
                        </Badge>
                      )}
                      <Badge
                        style={{
                          background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                          fontSize: "0.8rem",
                          padding: "6px 12px",
                        }}
                      >
                        <FaInfoCircle className="me-1" />
                        Codificación por empresa
                      </Badge>
                      {invalidBindingsCount > 0 && (
                        <Badge bg="danger" style={{ fontSize: "0.8rem", padding: "6px 12px" }}>
                          {invalidBindingsCount} vínculo(s) inválido(s)
                        </Badge>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <div className="d-flex gap-2 justify-content-end mb-3 flex-wrap">
              <Button
                variant="outline-secondary"
                onClick={() => navigate(-1)}
                className="px-3"
                style={{ borderRadius: "10px" }}
              >
                <FaArrowLeft className="me-2" /> Volver
              </Button>
              <Button
                variant="outline-dark"
                onClick={resetDefaults}
                disabled={saving || loading}
                className="px-3"
                style={{ borderRadius: "10px" }}
              >
                <FaSyncAlt className="me-2" /> Restaurar defaults
              </Button>
              <Button
                onClick={guardar}
                disabled={saving || loading || !ruc}
                className="px-4 text-white border-0"
                style={{
                  borderRadius: "10px",
                  background: saving
                    ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                    : "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
                }}
              >
                {saving ? <Spinner size="sm" className="me-2" /> : <FaSave className="me-2" />}
                Guardar Configuración
              </Button>
            </div>

            {error && (
              <Alert variant="danger" className="border-0 shadow-sm" style={{ borderRadius: "10px" }}>
                {error}
              </Alert>
            )}
            {okMsg && (
              <Alert variant="success" className="border-0 shadow-sm" style={{ borderRadius: "10px" }}>
                {okMsg}
              </Alert>
            )}

            <Row className="g-3">
              {FIELDS.map((section, index) => (
                <Col md={6} key={section.title}>
                  <Card className="mb-1 shadow-sm border-0 h-100" style={{ borderRadius: "15px" }}>
                    <Card.Header
                      className="text-white py-3"
                      style={{
                        background: SECTION_GRADIENTS[index % SECTION_GRADIENTS.length],
                        borderRadius: "15px 15px 0 0",
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <FaFileAlt className="me-2" />
                        <h5 className="mb-0 fw-semibold">{section.title}</h5>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                      {(catalog[section.id] || []).map((item) => (
                        <div
                          className="mb-3 p-3 rounded-3 border"
                          style={{
                            backgroundColor: "#fff",
                            borderColor: isSourcePathValidForSection(section.id, item.source_path) ? "#dee2e6" : "#dc3545",
                          }}
                          key={`${section.id}-${item.key}`}
                        >
                          <Row className="g-2 align-items-end">
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                  Descripción
                                </Form.Label>
                                <Form.Control
                                  value={item.label || ""}
                                  onChange={(e) => setLabel(section.id, item.key, e.target.value)}
                                  placeholder="Descripción del concepto"
                                  style={{ borderRadius: "10px" }}
                                />
                              </Form.Group>
                            </Col>
                            <Col md={4}>
                              <Form.Group>
                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                  Variable Planilla
                                </Form.Label>
                                <Form.Select
                                  value={item.source_path || getDefaultSourcePathForSection(section.id, item.key)}
                                  onChange={(e) => setSourcePath(section.id, item.key, e.target.value)}
                                  isInvalid={!isSourcePathValidForSection(section.id, item.source_path)}
                                  style={{ borderRadius: "10px" }}
                                >
                                  {getSourceOptionsForSection(section.id).map((option) => (
                                    <option key={option.value} value={option.value}>
                                      {option.label}
                                    </option>
                                  ))}
                                </Form.Select>
                                {!isSourcePathValidForSection(section.id, item.source_path) && (
                                  <Form.Text className="text-danger">
                                    Esta variable no corresponde a la sección {section.title}.
                                  </Form.Text>
                                )}
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Form.Group>
                                <Form.Label className="fw-semibold" style={{ color: "#8B4513" }}>
                                  Código
                                </Form.Label>
                                <InputGroup>
                                  <InputGroup.Text style={{ backgroundColor: "#f8f9fa" }}>#</InputGroup.Text>
                                  <Form.Control
                                    value={codes?.[section.key]?.[item.key] || ""}
                                    onChange={(e) => setCode(section.key, item.key, e.target.value)}
                                    placeholder="0121"
                                    maxLength={4}
                                    style={{ borderRadius: "0 10px 10px 0" }}
                                  />
                                </InputGroup>
                              </Form.Group>
                            </Col>
                            <Col md={2}>
                              <Button
                                variant="outline-danger"
                                className="w-100"
                                style={{ borderRadius: "10px" }}
                                onClick={() => removeField(section.id, section.key, item.key)}
                              >
                                <FaTrash />
                              </Button>
                            </Col>
                          </Row>
                        </div>
                      ))}
                      <Button
                        variant="outline-primary"
                        style={{ borderRadius: "10px" }}
                        onClick={() => addField(section.id, section.key)}
                      >
                        <FaPlus className="me-2" />
                        Agregar código
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ConfiguracionPdtPlame;
